import { useState, useRef, useCallback, useEffect } from 'react';
import { drawVideoFrame } from '../utils/videoFrame';

interface UseWebcamResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isActive: boolean;
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  capture: () => Promise<Blob | null>;
  availableCameras: MediaDeviceInfo[];
  selectedCamera: string | null;
  selectCamera: (deviceId: string) => Promise<void>;
}

interface UseWebcamOptions {
  autoStart?: boolean;
  preferredDeviceId?: string;
  facingMode?: 'user' | 'environment';
  width?: number;
  height?: number;
}

export const useWebcam = (options: UseWebcamOptions = {}): UseWebcamResult => {
  const {
    autoStart = false,
    preferredDeviceId,
    facingMode = 'user',
    width = 640,
    height = 480,
  } = options;

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(preferredDeviceId || null);

  // List available cameras
  const listCameras = useCallback(async () => {
    try {
      // Request permission first (needed to get device labels) - with timeout
      console.log(' Requesting camera permission to list devices...');
      const permissionStream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ video: true }),
        new Promise<MediaStream>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout ao solicitar permissão de câmera')), 5000)
        )
      ]);

      console.log(' Permission granted, stopping temporary stream');
      permissionStream.getTracks().forEach(track => track.stop());

      console.log(' Enumerating camera devices...');
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      console.log(' Found cameras:', cameras.length);
      setAvailableCameras(cameras);

      // Auto-select first camera if none selected
      if (!selectedCamera && cameras.length > 0) {
        console.log(' Auto-selecting first camera:', cameras[0].label || cameras[0].deviceId);
        setSelectedCamera(cameras[0].deviceId);
      }

      return cameras;
    } catch (err) {
      console.error('Failed to list cameras:', err);
      setError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
      return [];
    }
  }, [selectedCamera]);

  // Start webcam
  const start = useCallback(async () => {
    console.log(' Start webcam called', { isActive, isLoading });
    if (isActive || isLoading) {
      console.log(' Webcam already active or loading, skipping');
      return;
    }

    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error(' getUserMedia not supported');
      setError('Seu navegador não suporta acesso à câmera. Use a opção de upload de foto.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // List cameras if not already done
      if (availableCameras.length === 0) {
        console.log(' Listing cameras...');
        await listCameras();
      }

      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
          facingMode: selectedCamera ? undefined : facingMode,
          width: { ideal: width },
          height: { ideal: height },
        },
        audio: false,
      };

      console.log(' Requesting camera access with constraints:', constraints);

      // Add timeout for getUserMedia (10 seconds max)
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        new Promise<MediaStream>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout ao acessar câmera. Tente novamente ou use upload de foto.')), 10000)
        )
      ]);

      console.log(' Got media stream:', stream.id, 'tracks:', stream.getTracks().length);
      streamRef.current = stream;

      if (videoRef.current) {
        console.log(' Attaching stream to video element');
        const video = videoRef.current;
        video.srcObject = stream;
        console.log(' Stream attached to video element');

        // Set active immediately so UI shows the video feed faster
        setIsActive(true);

        // Wait for video metadata to be loaded before playing (with fast timeout)
        await Promise.race([
          new Promise<void>((resolve, reject) => {
            const handleLoadedMetadata = () => {
              video.removeEventListener('loadedmetadata', handleLoadedMetadata);
              video.removeEventListener('error', handleError);
              resolve();
            };
            const handleError = (e: Event) => {
              video.removeEventListener('loadedmetadata', handleLoadedMetadata);
              video.removeEventListener('error', handleError);
              reject(new Error('Video failed to load'));
            };

            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            video.addEventListener('error', handleError);

            // If already loaded
            if (video.readyState >= 1) {
              handleLoadedMetadata();
            }
          }),
          // Timeout after 300ms for faster startup
          new Promise<void>((resolve) => {
            setTimeout(() => {
              console.warn('Metadata loading timeout, continuing anyway');
              resolve();
            }, 300);
          })
        ]);

        // Try to play video
        try {
          await video.play();
          console.log('Video play() succeeded');
        } catch (playError) {
          console.warn('Video play() failed, but continuing:', playError);
        }

        // Minimal wait for video to start (reduced from 500ms for faster response)
        await new Promise(resolve => setTimeout(resolve, 50));

        // Check if video has valid dimensions
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          console.log('Video ready with dimensions:', {
            width: video.videoWidth,
            height: video.videoHeight,
            readyState: video.readyState
          });
        } else {
          console.warn('Video started but dimensions not available yet:', {
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            readyState: video.readyState
          });
        }
      } else {
        console.error(' videoRef.current is null! Cannot attach stream.');
      }

      console.log(' Webcam started successfully', { isActive: true, isReady: true });
      setIsReady(true);
    } catch (err: any) {
      console.error('Failed to start webcam:', err);

      if (err.name === 'NotAllowedError') {
        setError('Permissão para acessar a câmera foi negada');
      } else if (err.name === 'NotFoundError') {
        setError('Nenhuma câmera encontrada');
      } else if (err.name === 'NotReadableError') {
        setError('Câmera está em uso por outra aplicação');
      } else {
        setError('Erro ao acessar a câmera');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isActive, isLoading, selectedCamera, facingMode, width, height, availableCameras.length, listCameras]);

  // Stop webcam
  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsActive(false);
    setIsReady(false);
  }, []);

  // Capture a frame from the video
  const capture = useCallback(async (): Promise<Blob | null> => {
    if (!videoRef.current || !isActive) {
      console.error('Capture failed: video not active or ref is null');
      return null;
    }

    const video = videoRef.current;

    // Get video dimensions, with fallback to default size
    let width = video.videoWidth;
    let height = video.videoHeight;

    if (!width || !height || width === 0 || height === 0) {
      console.warn('Video dimensions invalid, using fallback size', {
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState
      });
      // Fallback to common webcam resolution
      width = 640;
      height = 480;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Capture failed: could not get canvas context');
      return null;
    }

    try {
      // Mirrored, because the preview is. Drawing the raw frame handed back a photo reversed
      // against what the operator had just framed.
      drawVideoFrame(ctx, video, { width, height, mirrored: true });

      // Convert to blob
      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error('Capture failed: canvas.toBlob returned null');
            } else {
              console.log('Capture successful, blob size:', blob.size);
            }
            resolve(blob);
          },
          'image/jpeg',
          0.75 // Quality (reduced from 0.92 for better performance)
        );
      });
    } catch (err) {
      console.error('Capture failed: error drawing to canvas', err);
      return null;
    }
  }, [isActive]);

  // Select a different camera
  const selectCamera = useCallback(async (deviceId: string) => {
    setSelectedCamera(deviceId);

    // If currently active, restart with new camera
    if (isActive) {
      stop();
      // Small delay to ensure previous stream is fully stopped
      await new Promise(resolve => setTimeout(resolve, 100));
      // The start will be called with the new selectedCamera value
    }
  }, [isActive, stop]);

  // Effect to restart camera when selected camera changes
  useEffect(() => {
    if (isActive && selectedCamera) {
      // Camera changed while active - restart
      const restartWithNewCamera = async () => {
        stop();
        await new Promise(resolve => setTimeout(resolve, 100));
        await start();
      };
      restartWithNewCamera();
    }
  }, [selectedCamera]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start if option is enabled
  useEffect(() => {
    if (autoStart) {
      start();
    }

    // Cleanup on unmount
    return () => {
      stop();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for device changes (e.g., camera plugged/unplugged)
  useEffect(() => {
    const handleDeviceChange = () => {
      listCameras();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [listCameras]);

  return {
    videoRef,
    canvasRef,
    isActive,
    isLoading,
    isReady,
    error,
    start,
    stop,
    capture,
    availableCameras,
    selectedCamera,
    selectCamera,
  };
};

export default useWebcam;
